import type { Metadata } from "next"
import Link from "next/link"
import { AlertTriangle, ArrowRight, Calculator, ShieldCheck } from "lucide-react"

import { Container } from "@/components/layout/container"
import { ProblemCard } from "@/components/problems/problem-card"
import {
  getAllProblems,
  getFeaturedProblems,
  getProblemCategoryStats,
} from "@/lib/problems"
import { absoluteUrl } from "@/lib/utils"
import { siteConfig } from "@/data/site"

export const metadata: Metadata = {
  title: "Проблеми домашньої консервації та готові рішення",
  description:
    "Практичний розділ Кухлика про типові проблеми домашніх заготовок: мутний розсіл, здута кришка, м’які огірки, помилки зі стерилізацією та зберіганням.",
  alternates: {
    canonical: absoluteUrl("/problems", siteConfig.url),
  },
  robots: {
    index: true,
    follow: true,
  },
  openGraph: {
    title: "Проблеми домашньої консервації та готові рішення",
    description:
      "Знайдіть короткі та практичні пояснення для типових проблем із домашніми заготовками, банками, маринадом і зберіганням.",
    url: absoluteUrl("/problems", siteConfig.url),
    type: "website",
    siteName: siteConfig.name,
    images: [
      {
        url: absoluteUrl(siteConfig.ogImage, siteConfig.url),
        width: 1200,
        height: 630,
        alt: "Проблеми та рішення для домашніх заготовок",
      },
    ],
  },
  twitter: {
    card: "summary_large_image",
    title: "Проблеми домашньої консервації та готові рішення",
    description:
      "Практичні рішення для типових проблем із домашніми заготовками та консервацією.",
    images: [absoluteUrl(siteConfig.ogImage, siteConfig.url)],
  },
}

const highlightItems = [
  {
    title: "Короткі відповіді без зайвої води",
    text: "Кожна сторінка проблеми побудована так, щоб швидко дати суть: що могло піти не так, що перевірити прямо зараз і коли краще не ризикувати.",
  },
  {
    title: "Сильна прив’язка до рецептів і гайдів",
    text: "Проблеми не існують окремо: поруч є релевантні рецепти, пояснення про стерилізацію, зберігання та корисні інструменти.",
  },
  {
    title: "Фокус на безпеці та передбачуваності",
    text: "Розділ допомагає не просто читати, а ухвалювати більш обережні й практичні рішення щодо домашніх заготовок.",
  },
]

export default async function ProblemsPage() {
  const [problems, featuredProblems, categoryStats] = await Promise.all([
    getAllProblems(),
    getFeaturedProblems(6),
    getProblemCategoryStats(),
  ])

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Проблеми та рішення Кухлика",
    itemListElement: problems.map((problem, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: absoluteUrl(`/problems/${problem.slug}`, siteConfig.url),
      name: problem.title,
    })),
  }

  return (
    <>
      <script
        id="problems-itemlist-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
      />

      <section className="border-b border-neutral-200 bg-gradient-to-b from-amber-50/70 to-white py-12 sm:py-16">
        <Container>
          <div className="max-w-4xl">
            <div className="eyebrow">
              <AlertTriangle className="mr-2 h-3.5 w-3.5" />
              Проблеми та рішення
            </div>

            <h1 className="mt-4 text-4xl font-semibold leading-tight text-neutral-950 sm:text-5xl">
              Типові проблеми домашньої консервації — коротко, практично і по суті
            </h1>

            <p className="mt-5 text-base leading-8 text-neutral-700 sm:text-lg">
              Цей розділ допомагає швидко розібратися, що могло піти не так із
              заготовками: мутний розсіл, нестабільний маринад, помилки зі
              стерилізацією, зберіганням або текстурою овочів. Тут зібрані
              короткі пояснення, тривожні сигнали та наступні корисні кроки.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link href="/guides" className="button-secondary">
                <ShieldCheck className="mr-2 h-4 w-4" />
                Гайди та поради
              </Link>

              <Link href="/calculators" className="button-primary">
                <Calculator className="mr-2 h-4 w-4" />
                Калькулятори пропорцій
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {categoryStats.length > 0 ? (
        <section className="py-10 sm:py-12">
          <Container>
            <div className="mb-8 max-w-3xl">
              <h2 className="text-2xl font-semibold text-neutral-950 sm:text-3xl">
                Категорії проблем
              </h2>

              <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
                Категорії допомагають згрупувати подібні проблеми і поступово
                формують окремі SEO-кластери всередині розділу.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {categoryStats.map((item) => (
                <Link key={item.category} href={item.href} className="tag-chip">
                  {item.label} ({item.count})
                </Link>
              ))}
            </div>
          </Container>
        </section>
      ) : null}

      {featuredProblems.length > 0 ? (
        <section className="pb-10 sm:pb-12">
          <Container>
            <div className="mb-8 max-w-3xl">
              <h2 className="text-2xl font-semibold text-neutral-950 sm:text-3xl">
                З чого почати
              </h2>

              <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
                Найкраще починати з найпоширеніших проблем, які часто викликають
                найбільше хвилювання після закрутки або під час зберігання.
              </p>
            </div>

            <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
              {featuredProblems.map((problem) => (
                <ProblemCard key={problem.id} problem={problem} />
              ))}
            </div>
          </Container>
        </section>
      ) : null}

      <section className="border-y border-neutral-200 bg-neutral-50 py-12 sm:py-14">
        <Container>
          <div className="grid gap-5 lg:grid-cols-3)">
            {highlightItems.map((item) => (
              <div
                key={item.title}
                className="rounded-[1.75rem] border border-neutral-200 bg-white p-5 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-neutral-950">{item.title}</h3>

                <p className="mt-3 text-sm leading-7 text-neutral-600">{item.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-12 sm:py-14">
        <Container>
          <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-3xl">
              <h2 className="text-2xl font-semibold text-neutral-950 sm:text-3xl">
                Усі проблеми
              </h2>

              <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
                Поступово цей розділ стане окремим SEO-кластером із короткими
                сторінками під конкретні запити користувачів.
              </p>
            </div>

            <Link href="/recipes" className="button-secondary">
              Перейти до рецептів
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
            {problems.map((problem) => (
              <ProblemCard key={problem.id} problem={problem} />
            ))}
          </div>
        </Container>
      </section>
    </>
  )
}